import { AfterContentChecked, ChangeDetectionStrategy, Component, ErrorHandler, EventEmitter, Input, OnDestroy, OnInit, Output, signal } from '@angular/core'
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { email, FieldTree, form, minLength, required, validate } from '@angular/forms/signals'

import { LaskuKorkoService } from '@lemon/core-firebase/service/lasku/lasku-korko.service'
import { LaskuChangeNoopCollector, LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { LaskuSpostiService } from '@lemon/core-firebase/service/lasku/lasku-sposti.service'
import { SahkoisenLaskunValittajaService, Verkkolaskuoperaattori } from '@lemon/core-firebase/service/lasku/sahkoisen-laskun-valittaja.service'
import { StringService } from '@lemon/core/service/string.service'

import { LaskuEmailEsikatselutiedot } from '../_jaettu-angular/laskut/esikatselu/email.perinteinen.component'
import { arvonMuutokset, asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FieldErrors, FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'
import { TimestampService } from '../_jaettu-angular/service/timestamp-service'

import { LaskuService } from '../_angular/service/lasku/lasku.service'

import {
  EmailLahetysStatus,
  EmailLahetysStatusKoodi,
  Lasku,
  LaskuasetuksetSpostille,
  LaskuBase,
  LaskunLahetysdata,
  LaskunLahetystapa,
  LaskunSahkoinenOsoite,
  LaskunumeroTyyppi,
  PeppolValidaatioTulos,
} from '@lemon/core-firebase/model/lasku'

import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { Kirjanpitaja, KirjanpitajanNimitiedot, KirjanpitajanTiimi } from 'app/_jaettu-lemonator/model/kirjanpitaja'
import { KirjanpitajanTiimiService } from 'app/_jaettu-lemonator/service/kirjanpitajan-tiimi.service'
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, of, ReplaySubject, Subject } from 'rxjs'
import { debounceTime, map, startWith, switchMap, takeUntil, throttleTime } from 'rxjs/operators'

export interface LaskunLahetystiedot {
  error: any
  tapa: LaskunLahetystapa
  lahetettyJuuri: Lasku
  lahetettyKasiteltava: LaskuBase
}

export interface SahkoinenLomake {
  osoite: string
  valittaja: string | null
}

export interface KuluLomake {
  huomautuskulu: number | null
  viivastyskorko: boolean
}

export interface EmailLomake {
  aihe: string
  otsikko: string
  teksti: string
  lisaavastaanottaja: string
}

export interface LaskunOmistajaLomake {
  tilastoKirjanpitaja: KirjanpitajanNimitiedot | null
  tilastoTiimi: KirjanpitajanTiimi | null
}

@Component({
  selector: 'app-laheta-lemonator',
  templateUrl: './laheta-lemonator.component.html',
  styleUrls: ['./laheta-lemonator.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LahetaLemonatorComponent implements OnInit, AfterContentChecked, OnDestroy {
  private spostiasetukset: LaskuasetuksetSpostille = null
  private tilaaEsikatseluPaivitysSubject: ReplaySubject<number> = new ReplaySubject(1)
  private ngUnsubscribe = new Subject<void>()

  @Output() laskuLahetettiin = new EventEmitter<LaskunLahetystiedot>()

  private _aktiivinenValilehti: number = 0
  get aktiivinenValilehti(): number {
    return this._aktiivinenValilehti
  }
  @Input()
  set aktiivinenValilehti(indeksi: number) {
    this._aktiivinenValilehti = indeksi
  }

  private _data: LaskunLahetysdata
  get data(): LaskunLahetysdata {
    return this._data
  }
  @Input()
  set data(data: LaskunLahetysdata) {
    this._data = data
    this._lahetysdata.set(data)
    this.onkoMuistutus = !!this._data?.muistutus
    this.onkoHyvitys = this._data?.kasiteltava?.nrotyyppi === LaskunumeroTyyppi.HYVITYS
    this.onkoUudelleenlahetys = !!this._data?.uudelleenlahetys
    this.alustaTiedot(this.onkoMuistutus, data)
  }

  onkoMuistutus: boolean = false
  onkoHyvitys: boolean = false

  get infotekstinAvain(): string {
    if (this.onkoHyvitys) {
      return 'info-hyvitys'
    }
    return this.onkoMuistutus ? 'info-muistutus' : 'info-tavallinen'
  }

  onkoUudelleenlahetys: boolean = false
  esikatselutiedot: ReplaySubject<LaskuEmailEsikatselutiedot> = new ReplaySubject(1)
  verkkolaskuoperaattorit: Verkkolaskuoperaattori[]
  emailLomake: FieldTree<EmailLomake>
  sahkoinenLomake: FieldTree<SahkoinenLomake>
  kuluLomake: FieldTree<KuluLomake>
  omistajaLomake: FieldTree<LaskunOmistajaLomake>
  asetuksissaOnOletustietojaVirhe = false
  fieldErrors: FieldErrors = {}
  osoitehaunHintTeksti: string = ''
  // osoitehaunTulokset: ApixHaeVerkkolaskuosoiteTulos[] = null
  namename = 'yoruenf' + Math.random()

  korko: number = 0
  viivastyskorkoLisataan: boolean = false
  kirjanpitajienNimiTiedotObservable: Observable<KirjanpitajanNimitiedot[]>
  tiimitObservable: Observable<KirjanpitajanTiimi[]>

  private _buttonPushInflight: boolean = false
  private readonly _lahetysdata = signal<LaskunLahetysdata>(null)
  private readonly _vastaanottajatPuuttuvat = signal(false)
  private peppolValidationSubject: BehaviorSubject<PeppolValidaatioTulos> = new BehaviorSubject(null)
  peppolValidationObservable: Observable<PeppolValidaatioTulos> = this.peppolValidationSubject.asObservable()

  email = {
    aihe: '',
    otsikko: '',
    teksti: '',
    vastaanottajat: [],
  }

  sahkoinen = {
    ytunnus: '',
    osoite: '',
    valittaja: '',
  }

  pyydaPaivitys() {
    if (this.aktiivinenValilehti === 0) {
      this.tilaaEsikatseluPaivitysSubject.next(1)
    }
  }

  annaOsoitteenValidointiFunktio(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value) {
        const ctrlValue = this._stringService.removeAllWhiteSpaces(control.value)

        if (this._lahetysdata().kasiteltava?.asiakas?.nimi?.toLowerCase()?.split(' ')?.includes('apix')) {
          // Special exception to enable sending invoices to Apix
          const apixTunnus = '003723327487'
          const apixValittuValittajana = this.sahkoinenLomake.valittaja().value()?.trim() === apixTunnus

          if (apixValittuValittajana && control.value === apixTunnus) {
            return null
          }
        }

        const valittajat = this.verkkolaskuoperaattorit || []
        for (const valittaja of valittajat) {
          const valittajaTunnus = this._stringService.removeAllWhiteSpaces(valittaja.tunnus)
          if (valittajaTunnus === ctrlValue) {
            return { valittaja: true }
          }
        }
      }
      return null
    }
  }

  constructor(
    private _validationService: FormValidationService,
    private _laskuSpostiService: LaskuSpostiService,
    private _laskuService: LaskuService,
    private _errorHandler: ErrorHandler,
    private _laskuSharedService: LaskuSharedService,
    private _ladataanService: LadataanService,
    private _timestampService: TimestampService,
    private _sahkoisenLaskunValittajaService: SahkoisenLaskunValittajaService,
    private _laskuKorkoService: LaskuKorkoService,
    private _stringService: StringService,
    private _lemonTranslation: LemonTranslationService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _kirjanpitajanTiimiService: KirjanpitajanTiimiService,
  ) {
    this.sahkoinenLomake = form(signal<SahkoinenLomake>({ osoite: '', valittaja: '' }), polku => {
      required(polku.osoite)
      validoiReaktiivisesti(polku.osoite, this.annaOsoitteenValidointiFunktio())
      minLength(polku.osoite, 9, { when: ({ valueOf }) => valueOf(polku.valittaja)?.trim() === 'PEPPOL' })
      required(polku.valittaja)
    })

    this.kuluLomake = form(signal<KuluLomake>({ huomautuskulu: 0, viivastyskorko: this.viivastyskorkoLisataan }), polku => {
      required(polku.huomautuskulu)
      validoiReaktiivisesti(polku.viivastyskorko, Validators.required)
    })

    this.emailLomake = form(signal<EmailLomake>({ aihe: this.email.aihe, otsikko: this.email.otsikko, teksti: this.email.teksti, lisaavastaanottaja: '' }), polku => {
      required(polku.aihe)
      required(polku.otsikko)
      required(polku.teksti)
      // Reaktiivisen lomakkeen setErrors korvasi kentän muut virheet, kunnes kenttä validoitiin uudelleen
      email(polku.lisaavastaanottaja, { when: ({ value }) => !this._vastaanottajatPuuttuvat() && !!value()?.trim() })
      validate(polku.lisaavastaanottaja, () => (this._vastaanottajatPuuttuvat() ? { kind: 'min' } : null))
    })

    this.omistajaLomake = form(signal<LaskunOmistajaLomake>({ tilastoKirjanpitaja: null, tilastoTiimi: null }), polku => {
      required(polku.tilastoKirjanpitaja)
      required(polku.tilastoTiimi)
    })

    this.verkkolaskuoperaattorit = this._sahkoisenLaskunValittajaService.annaKaikkiValittajat()

    // this.sahkoinenForm.get('ytunnus').valueChanges.pipe(
    //   debounceTime(1000)
    // ).subscribe((value: string) => {
    //   if (value) {
    //     const kasitelty = this.validationService.processValue(value)
    //     this.osoitehaunHintTeksti = 'Haetaan verkkolaskuosoitetta...'

    //     const pyynto: ApixHaeVerkkolaskuosoitettaPyynto = {
    //       ytunnus: kasitelty
    //     }

    //     const callable = this.lemontreeFunctions.httpsCallable<ApixHaeVerkkolaskuosoitettaPyynto, ApixHaeVerkkolaskuosoitettaVastaus>('apixHaeVerkkolaskuosoite')
    //     callable(pyynto).then(tulos => {

    //       if (tulos && tulos.tulokset && tulos.tulokset.length > 0) {
    //         this.osoitehaunTulokset = tulos.tulokset
    //       } else {
    //         this.osoitehaunTulokset = null
    //       }
    //       this.osoitehaunHintTeksti = ''

    //     }).catch(error => {
    //       this.osoitehaunHintTeksti = 'Haun aikana tapahtui virhe.'
    //       this.errorHandler.handleError(error)
    //     })
    //   }
    // })
    // this.sahkoinenForm.get('ytunnus').valueChanges.subscribe(value => {
    //   this.sahkoinen.ytunnus = value
    // })

    combineLatest([arvonMuutokset(this.sahkoinenLomake.osoite), arvonMuutokset(this.sahkoinenLomake.valittaja)])
      .pipe(
        debounceTime(500),
        switchMap(([osoite, valittaja]) => {
          if (valittaja !== 'PEPPOL') {
            return of(null)
          }

          if (!osoite || osoite.trim().length < 9) {
            return of(null)
          }
          return from(this._laskuService.validoiPeppolOsoite(osoite, this._lemonTranslation.nykyinenKieli)).pipe(
            startWith({ error: false, message: this._lemonTranslation.lokalisoi('lasku.peppol-validaatio.tarkistetaan', this._lemonTranslation.nykyinenKieli) }),
          )
        }),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(value => {
        this.peppolValidationSubject.next(value)
      })

    arvonMuutokset(this.sahkoinenLomake.osoite)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.sahkoinen.osoite = value?.trim() ?? null
      })

    arvonMuutokset(this.sahkoinenLomake.valittaja)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.sahkoinen.valittaja = value?.trim() ?? null
        // Validaattorien vaihdon jälkeinen updateValueAndValidity lähetti osoitteen uudelleen, mihin peppol-tarkistus nojaa
        asetaArvo(this.sahkoinenLomake.osoite, this.sahkoinenLomake.osoite().value())
      })

    // Kulu form
    arvonMuutokset(this.kuluLomake.huomautuskulu).subscribe(value => {
      if (this.data.huomautuskulu) {
        this.data.huomautuskulu.hinta = value
        this.tilaaEsikatseluPaivitysSubject.next(1)
      }
    })
    arvonMuutokset(this.kuluLomake.viivastyskorko).subscribe(value => {
      this.viivastyskorkoLisataan = value
      if (this.data.korkokulu) {
        this.data.korkokulu.hinta = this.viivastyskorkoLisataan ? this.korko : 0
        this.tilaaEsikatseluPaivitysSubject.next(1)
      }
    })

    // Email form
    arvonMuutokset(this.emailLomake.aihe).subscribe(value => {
      this.email.aihe = value || ''
      this.data.kasiteltava.email.aihe = this.email.aihe
      this.tilaaEsikatseluPaivitysSubject.next(1)
    })
    arvonMuutokset(this.emailLomake.otsikko).subscribe(value => {
      this.email.otsikko = value || ''
      this.data.kasiteltava.email.otsikko = this.email.otsikko
      this.tilaaEsikatseluPaivitysSubject.next(1)
    })
    arvonMuutokset(this.emailLomake.teksti).subscribe(value => {
      this.email.teksti = value || ''
      this.data.kasiteltava.email.teksti = this.email.teksti
      this.tilaaEsikatseluPaivitysSubject.next(1)
    })
    arvonMuutokset(this.emailLomake.lisaavastaanottaja).subscribe(value => {
      this._vastaanottajatPuuttuvat.set(false)
      if (value && (value.indexOf(',') !== -1 || value.indexOf(' ') !== -1)) {
        this.lisaaEmail(this.emailLomake.lisaavastaanottaja)
      }
    })

    // Omistaja form
    arvonMuutokset(this.omistajaLomake.tilastoKirjanpitaja)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (value && this.data?.kasiteltava?.tilastot) {
          this.data.kasiteltava.tilastot.kirjanpitaja = value.avain
        }
      })
    arvonMuutokset(this.omistajaLomake.tilastoTiimi)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (value && this.data?.kasiteltava?.tilastot) {
          this.data.kasiteltava.tilastot.tiimi = value.avain
        }
      })

    this.tilaaEsikatseluPaivitysSubject.pipe(takeUntil(this.ngUnsubscribe), throttleTime(250)).subscribe(() => {
      if (this.data.asetukset && this.data.juurilasku && this.data.kasiteltava) {
        this._laskuSharedService.paivitaRyppaanJaKasiteltavanSummat(this.data.juurilasku, this.data.kasiteltava, this.data.reskontra, new LaskuChangeNoopCollector())
        this.esikatselutiedot.next({
          asetukset: this.data.asetukset,
          juurilasku: this.data.juurilasku,
          kasiteltava: this.data.kasiteltava,
          maksumuistutus: false,
        })
      }
    })

    this.kirjanpitajienNimiTiedotObservable = this._kirjanpitajaService.kirjanpitajienNimitiedotObservable.pipe(
      map(kirjanpitajat =>
        kirjanpitajat
          .filter(k => {
            return (
              k.avain !== 'uekRGWgeLqecXk0Gh8el0s85SEt1' && // Taneli Testinen
              k.avain !== 'QgPvtcCjoOdf6Zg7lgMwqLWp2BG2'
            ) // Herra Holvi
          })
          .sort((a, b) => a.etunimi.localeCompare(b.etunimi) || a.sukunimi.localeCompare(b.sukunimi)),
      ),
    )

    this.tiimitObservable = this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(
      switchMap(k => {
        return this._kirjanpitajanTiimiService.annaKaikkiTiimit()
      }),
    )
  }

  ngAfterContentChecked() {
    this.fieldErrors = this._validationService.updateValidationStatus('lasku.sposti', this.emailLomake)
  }

  // private _filter(value: string | Verkkolaskuoperaattori): Verkkolaskuoperaattori[] {
  //   if (value === '' || !value) {
  //     return []
  //   }
  //   let filterValue = ''
  //   if (value instanceof String || typeof value === 'string') {
  //     filterValue = value.toLowerCase()
  //   } else {
  //     filterValue = value && value.nimi ? value.nimi.toLowerCase() : ''
  //   }
  //   return this._sahkoisenLaskunValittajaService.annaKaikkiValittajat().filter(operaattori => operaattori.nimi.toLowerCase().includes(filterValue) || operaattori.tunnus.toLowerCase().includes(filterValue))
  // }

  valittajaDisplayFn(operaattori?: Verkkolaskuoperaattori): string | undefined {
    if (operaattori && operaattori.nimi) {
      return operaattori.nimi + ' (' + operaattori.tunnus + ')'
    }
    if (operaattori instanceof String || typeof operaattori === 'string') {
      return operaattori as any as string
    }
    return undefined
  }

  ngOnInit() {
    this.tilaaEsikatseluPaivitysSubject.next(1)
  }

  private _annaOletuspohjanAvain(onkoMuistutus: boolean, kasiteltava: LaskuBase): string {
    if (kasiteltava.nrotyyppi === LaskunumeroTyyppi.HYVITYS) {
      return 'hyvityssposti'
    }
    return onkoMuistutus ? 'muistutussposti' : 'sposti'
  }

  private async alustaTiedot(onkoMuistutus: boolean, data: LaskunLahetysdata) {
    // Tyhjennä lomakkeet:
    this.email = {
      aihe: '',
      otsikko: '',
      teksti: '',
      vastaanottajat: [],
    }
    this.sahkoinen = {
      ytunnus: '',
      osoite: '',
      valittaja: '',
    }

    if (!data.asetukset) {
      return
    }

    // Alusta email
    if (!data.asetukset.spostiasetukset) {
      data.asetukset.spostiasetukset = {}
    }

    this.spostiasetukset = this._laskuSpostiService.annaSpostiAsetukset(data.kasiteltava, onkoMuistutus, data.asetukset)

    const oletuspohja = this._annaOletuspohjanAvain(onkoMuistutus, data.kasiteltava)
    this.email.aihe = this.korvaaMuuttujat(this.spostiasetukset.aihe || this._laskuSharedService.annaLokalisoituMerkkijono(`lasku.spostipohjat.oletukset.${oletuspohja}.aihe`, data.kasiteltava))
    this.email.otsikko = this.korvaaMuuttujat(
      this.spostiasetukset.otsikko || this._laskuSharedService.annaLokalisoituMerkkijono(`lasku.spostipohjat.oletukset.${oletuspohja}.otsikko`, data.kasiteltava),
    )
    this.email.teksti = this.korvaaMuuttujat(this.spostiasetukset.teksti || this._laskuSharedService.annaLokalisoituMerkkijono(`lasku.spostipohjat.oletukset.${oletuspohja}.teksti`, data.kasiteltava))

    if (data.kasiteltava.asiakas.laskunVastaanottajat) {
      for (const vastaanottaja of data.kasiteltava.asiakas.laskunVastaanottajat) {
        this.email.vastaanottajat.push(vastaanottaja)
      }
    }

    if (!data.kasiteltava.email) {
      data.kasiteltava.email = {
        start: this._timestampService.now(),
        done: null,
        aihe: '',
        otsikko: '',
        teksti: '',
        slogan: '',
        template: null,
        vastaanottajat: null,
      }
    }
    data.kasiteltava.email.aihe = this.email.aihe
    data.kasiteltava.email.otsikko = this.email.otsikko
    data.kasiteltava.email.teksti = this.email.teksti
    data.kasiteltava.email.vastaanottajat = this.muodostaVastaanottaja()
    data.kasiteltava.email.footer = 'footer2'
    data.kasiteltava.email.slogan = this.data.asetukset.slogan || ''

    asetaArvo(this.emailLomake.aihe, this.email.aihe ?? '')
    asetaArvo(this.emailLomake.otsikko, this.email.otsikko ?? '')
    asetaArvo(this.emailLomake.teksti, this.email.teksti ?? '')

    // Alusta sähköinen lasku
    this.sahkoinen.ytunnus = data.kasiteltava.asiakas.ytunnus ? data.kasiteltava.asiakas.ytunnus : ''

    if (!data.kasiteltava.asiakas.sahkoinenosoite) {
      data.kasiteltava.asiakas.sahkoinenosoite = {
        sahkoinenOsoite: '',
        sahkoinenValittaja: '',
      }
    }

    if (!data.kasiteltava.asiakas.sahkoinenosoite.sahkoinenOsoite) {
      data.kasiteltava.asiakas.sahkoinenosoite.sahkoinenOsoite = ''
    }

    if (!data.kasiteltava.asiakas.sahkoinenosoite.sahkoinenValittaja) {
      data.kasiteltava.asiakas.sahkoinenosoite.sahkoinenValittaja = ''
    }

    this.sahkoinen.osoite = data.kasiteltava.asiakas.sahkoinenosoite.sahkoinenOsoite
    asetaArvo(this.sahkoinenLomake.osoite, data.kasiteltava.asiakas.sahkoinenosoite.sahkoinenOsoite ?? '')

    const unconfirmedValittajaTunnus = data.kasiteltava.asiakas.sahkoinenosoite.sahkoinenValittaja
    const confirmedValittajaTunnus = unconfirmedValittajaTunnus ? this._parseAndConfirmValittajaTunnus(unconfirmedValittajaTunnus) : null
    asetaArvo(this.sahkoinenLomake.valittaja, confirmedValittajaTunnus)

    // Alusta korkokulut
    if (onkoMuistutus && !this.onkoUudelleenlahetys) {
      this.korko = this._laskuKorkoService.laskeKorkoLaskulle(data.juurilasku, data.kasiteltava, data.asetukset, data.reskontra)
      this.data.korkokulu.hinta = this.viivastyskorkoLisataan ? this.korko : 0
      this.data.huomautuskulu.hinta = 0
    }

    // Alusta laskun omistaja
    if (!this.onkoUudelleenlahetys) {
      await this._asetaTilastoTiedot(data.kasiteltava)
    }
  }

  private async _asetaTilastoTiedot(kasiteltava: LaskuBase) {
    // Varmista, että on tyhjä objekti
    if (!kasiteltava.tilastot) {
      kasiteltava.tilastot = { kirjanpitaja: null, tiimi: null }
    }

    // Asetetaan aina kirjautunut kirjanpitäjä oletuksena
    const kirjautunutKirjanpitaja = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)
    kasiteltava.tilastot.kirjanpitaja = kirjautunutKirjanpitaja.uid

    // Aseta laskuun tiimi
    const kaikkiTiimit = await firstValueFrom(this.tiimitObservable)
    const tiimi = kaikkiTiimit.find(
      suspect =>
        suspect.esihenkilot.some(e => e.kirjanpitajaAvain === kasiteltava.tilastot.kirjanpitaja && e.primary) ||
        suspect.jasenet.some(j => j.kirjanpitajaAvain === kasiteltava.tilastot.kirjanpitaja && j.primary),
    )
    if (tiimi) {
      kasiteltava.tilastot.tiimi = tiimi.avain
    }

    // Alusta form laskun kirjanpitäjällä
    if (kasiteltava.tilastot.kirjanpitaja) {
      const kirjanpitajienNimitiedot = await firstValueFrom(this.kirjanpitajienNimiTiedotObservable)
      const kirjanpitaja = kirjanpitajienNimitiedot.find(k => k.avain === kasiteltava.tilastot.kirjanpitaja)
      if (kirjanpitaja) {
        asetaArvo(this.omistajaLomake.tilastoKirjanpitaja, kirjanpitaja)
      }
    }

    // Alusta form laskun tiimillä
    if (kasiteltava.tilastot.tiimi) {
      const team = kaikkiTiimit.find(t => t.avain === kasiteltava.tilastot.tiimi)
      if (team) {
        asetaArvo(this.omistajaLomake.tilastoTiimi, team)
      }
    }
  }

  compareTiimit(tiimi1: KirjanpitajanTiimi, tiimi2: KirjanpitajanTiimi) {
    return tiimi1?.avain === tiimi2?.avain
  }

  compareKirjanpitajat(kp1: KirjanpitajanNimitiedot, kp2: KirjanpitajanNimitiedot) {
    return kp1?.avain === kp2?.avain
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  lisaa() {
    this._vastaanottajatPuuttuvat.set(false)
    this.lisaaEmail(this.emailLomake.lisaavastaanottaja)
  }

  private lisaaEmail(field: FieldTree<string>) {
    const value = field().value()
    // console.log('raaka', value)
    if ((value || '').trim()) {
      let arvo = value.trim()
      arvo = arvo.replace(/,/g, '').trim()
      // console.log('kasitelty', arvo)
      if (!Validators.email({ value: arvo } as AbstractControl)) {
        this.email.vastaanottajat.push(arvo)
        asetaArvo(field, '')
        this.data.kasiteltava.email.vastaanottajat = this.muodostaVastaanottaja()
        this.tilaaEsikatseluPaivitysSubject.next(1)
      }
    }
  }

  poistaAsiakas(vastaanottaja: string): void {
    const index = this.email.vastaanottajat.indexOf(vastaanottaja)
    if (index >= 0) {
      this.email.vastaanottajat.splice(index, 1)
    }
    this.data.kasiteltava.email.vastaanottajat = this.muodostaVastaanottaja()
    this.tilaaEsikatseluPaivitysSubject.next(1)
  }

  private muodostaVastaanottaja() {
    const vastaanottajat: EmailLahetysStatus[] = []
    for (const vastaanottaja of this.email.vastaanottajat) {
      vastaanottajat.push({
        email: vastaanottaja.trim().toLowerCase(),
        status: EmailLahetysStatusKoodi.PROSESSOIDAAN,
        viesti: null,
      })
    }
    return vastaanottajat
  }

  async lahetaSahkoinen() {
    if (!this.sahkoinen || !this.sahkoinen.osoite || !this.sahkoinenLomake().valid()) {
      const formErrors = [this.sahkoinenLomake.osoite, this.sahkoinenLomake.valittaja].filter(kentta => kentta().errors().length)

      const peppolErrorObject = await firstValueFrom(this.peppolValidationObservable)
      const peppolError = !!peppolErrorObject?.error
      if ((!peppolError && formErrors.length > 0) || (peppolError && formErrors.length > 1)) {
        this._validationService.merkitseKokoLomakeKosketuksi(this.sahkoinenLomake)
        return
      }
    }

    if (this._buttonPushInflight) {
      return
    }
    this._buttonPushInflight = true
    try {
      // Päivitä tiedot
      // this.data.kasiteltava.asiakas.ytunnus = this.sahkoinen.ytunnus

      this._ladataanService.aloitaLataaminen()

      const kirjanpitaja: Kirjanpitaja = await firstValueFrom(this._kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable)
      const asiakas = await this._laskuService.haeAsiakas(this.data.juurilasku)

      if (this.onkoUudelleenlahetys) {
        const osoite: LaskunSahkoinenOsoite = { sahkoinenOsoite: this.sahkoinen.osoite, sahkoinenValittaja: this.sahkoinen.valittaja }
        await this._laskuService
          .lahetaUudelleenSahkoisesti(asiakas, osoite, kirjanpitaja, this.data.juurilasku, this.data.kasiteltava)
          .then(({ juurilasku, kasiteltava }) => {
            this.data.juurilasku = juurilasku
            this.data.kasiteltava = kasiteltava
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.SAHKOINEN, lahetettyJuuri: juurilasku, lahetettyKasiteltava: kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      } else if (this.onkoMuistutus) {
        const osoite: LaskunSahkoinenOsoite = { sahkoinenOsoite: this.sahkoinen.osoite, sahkoinenValittaja: this.sahkoinen.valittaja }
        await this._laskuService
          .merkitseLaskuLahetetyksiSahkoisesti(asiakas, osoite, kirjanpitaja, this.data.juurilasku, this.data.kasiteltava)
          .then(() => {
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.SAHKOINEN, lahetettyJuuri: this.data.juurilasku, lahetettyKasiteltava: this.data.kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      } else {
        const osoite: LaskunSahkoinenOsoite = { sahkoinenOsoite: this.sahkoinen.osoite, sahkoinenValittaja: this.sahkoinen.valittaja }
        await this._laskuService
          .merkitseLaskuLahetetyksiSahkoisesti(asiakas, osoite, kirjanpitaja, this.data.juurilasku, this.data.kasiteltava)
          .then(() => {
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.SAHKOINEN, lahetettyJuuri: this.data.juurilasku, lahetettyKasiteltava: this.data.kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      }
    } catch (error) {
      this._errorHandler.handleError(error)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._buttonPushInflight = false
    }
  }

  async saveAndPrint() {
    this._ladataanService.aloitaLataaminen()

    if (this._buttonPushInflight) {
      return
    }
    this._buttonPushInflight = true
    try {
      const kirjanpitaja: Kirjanpitaja = await firstValueFrom(this._kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable)
      const asiakas = await this._laskuService.haeAsiakas(this.data.juurilasku)

      if (this.onkoUudelleenlahetys) {
        await this._laskuService
          .lahetaUudelleenTulostamalla(asiakas, kirjanpitaja, this.data.juurilasku, this.data.kasiteltava)
          .then(({ juurilasku, kasiteltava }) => {
            this.data.juurilasku = juurilasku
            this.data.kasiteltava = kasiteltava
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.ITSE, lahetettyJuuri: juurilasku, lahetettyKasiteltava: kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      } else if (this.onkoMuistutus) {
        await this._laskuService
          .merkitseLaskunMaksumuistutusLahetetyksiTulostamalla(asiakas, kirjanpitaja, this.data.juurilasku, this.data.kasiteltava)
          .then(result => {
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.ITSE, lahetettyJuuri: this.data.juurilasku, lahetettyKasiteltava: this.data.kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      } else {
        await this._laskuService
          .merkitseLaskuLahetetyksiTulostamalla(asiakas, kirjanpitaja, this.data.juurilasku, this.data.kasiteltava)
          .then(result => {
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.ITSE, lahetettyJuuri: this.data.juurilasku, lahetettyKasiteltava: this.data.kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      }
    } catch (error) {
      this._errorHandler.handleError(error)
      console.log(error)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._buttonPushInflight = false
    }
  }

  async send() {
    if (!this.email || !this.email.vastaanottajat || this.email.vastaanottajat.length < 1) {
      this._vastaanottajatPuuttuvat.set(true)
      return
    }

    if (this._buttonPushInflight) {
      return
    }
    this._buttonPushInflight = true
    try {
      this._ladataanService.aloitaLataaminen()
      const vastaanottajat: EmailLahetysStatus[] = this.muodostaVastaanottaja()

      const kirjanpitaja: Kirjanpitaja = await firstValueFrom(this._kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable)
      const asiakas = await this._laskuService.haeAsiakas(this.data.juurilasku)

      const postmarkTemplateId = this._laskuSpostiService.annaSahkopostipohjanPostmarkNumero(this.spostiasetukset.template, this.data.kasiteltava.kieli)

      if (this.onkoUudelleenlahetys) {
        await this._laskuService
          .lahetaUudelleenSpostilla(
            asiakas,
            kirjanpitaja,
            postmarkTemplateId,
            this.data.juurilasku,
            this.data.kasiteltava,
            vastaanottajat,
            this.email.aihe,
            this.email.otsikko,
            this.email.teksti,
            this.data.asetukset.slogan,
          )
          .then(({ juurilasku, kasiteltava }) => {
            this.data.juurilasku = juurilasku
            this.data.kasiteltava = kasiteltava
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.SAHKOPOSTI, lahetettyJuuri: juurilasku, lahetettyKasiteltava: kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      } else if (this.onkoMuistutus) {
        await this._laskuService
          .merkitseLaskunMaksumuistutusLahetetyksiSpostilla(
            asiakas,
            kirjanpitaja,
            postmarkTemplateId,
            this.data.juurilasku,
            this.data.kasiteltava,
            vastaanottajat,
            this.email.aihe,
            this.email.otsikko,
            this.email.teksti,
            this.data.asetukset.slogan,
            true,
          )
          .then(result => {
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.SAHKOPOSTI, lahetettyJuuri: this.data.juurilasku, lahetettyKasiteltava: this.data.kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      } else {
        await this._laskuService
          .merkitseLaskuLahetetyksiSpostilla(
            asiakas,
            kirjanpitaja,
            postmarkTemplateId,
            this.data.juurilasku,
            this.data.kasiteltava,
            vastaanottajat,
            this.email.aihe,
            this.email.otsikko,
            this.email.teksti,
            this.data.asetukset.slogan,
            null,
          )
          .then(result => {
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.SAHKOPOSTI, lahetettyJuuri: this.data.juurilasku, lahetettyKasiteltava: this.data.kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      }
    } catch (error) {
      this._errorHandler.handleError(error)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._buttonPushInflight = false
    }
  }

  private korvaaMuuttujat(korvattava: string): string {
    return this._laskuSpostiService.korvaaMuuttujat(korvattava, this.data.asetukset, this.data.juurilasku, this.data.kasiteltava)
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  // This form field used to be a free-text input, so extra validation is needed here to catch invalid operator ID-s in legacy data.
  private _parseAndConfirmValittajaTunnus(unconfirmed: string) {
    if (!unconfirmed) {
      return null
    }
    if (unconfirmed['tunnus']) {
      return unconfirmed['tunnus']
    }
    if (!unconfirmed['toUpperCase']) {
      return null
    }
    const upperCase = unconfirmed.toUpperCase()
    const found: Verkkolaskuoperaattori = this._sahkoisenLaskunValittajaService.annaValittaja(upperCase)
    return found?.tunnus ?? null
  }
}
